import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import "../../styles/HomeStyling/ResetPassword.css";

export const ResetPassword = ({ email, onBack }) => {
  const navigate = useNavigate();
  const Back = `${process.env.PUBLIC_URL}/assets/logos/back.svg`;

  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");
  const [loading, setLoading] = useState(false);

  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState("Weak ❌");

  // Password constraint logic
  const constraints = useMemo(() => ({
    minLength: newPassword.length >= 8,
    hasUpper: /[A-Z]/.test(newPassword),
    hasLower: /[a-z]/.test(newPassword),
    hasNumber: /\d/.test(newPassword),
    hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(newPassword),
  }), [newPassword]);

  const constraintsMetCount = useMemo(() => {
    return Object.values(constraints).filter(Boolean).length;
  }, [constraints]);

  const passwordsMatch =
    newPassword && confirmPassword && newPassword === confirmPassword;

  const isFormComplete =
    otp && newPassword && confirmPassword && passwordsMatch && constraintsMetCount >= 3;

  useEffect(() => {
    if (constraintsMetCount < 3) {
      setPasswordStrength("Weak ❌");
    } else if (constraintsMetCount < 5) {
      setPasswordStrength("Medium ⚠️");
    } else {
      setPasswordStrength("Strong ✅");
    }
  }, [constraintsMetCount]);

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!isFormComplete) return;

    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/reset_password`,
        {
          email,
          otp,
          new_password: newPassword,
        }
      );
      setMessage(response.data.message);
      if (response.data.success) {
        setMessageType("success");
        setTimeout(() => navigate("/login"), 1500);
      } else {
        setMessageType("error");
      }
    } catch (err) {
      setMessage("An error occurred. Please try again.");
      setMessageType("error");
    } finally {
      setLoading(false);
    }
  };

  // OTP Timer Logic
  const [timer, setTimer] = useState(0);
  const [requestOtpError, setRequestOtpError] = useState("");
  const [requestOtpSuccess, setRequestOtpSuccess] = useState("");

  const handleRequestNewOtp = async () => {
    if (timer > 0) return;
    setRequestOtpError("");
    setRequestOtpSuccess("");
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/request_otp`,
        { email },
        { withCredentials: true }
      );
      if (response.data.success) {
        setRequestOtpSuccess("New OTP sent successfully.");
        setTimer(60);
      } else {
        setRequestOtpError(response.data.message || "Failed to send new OTP.");
      }
    } catch (err) {
      setRequestOtpError(
        err.response?.data?.message || "Network error. Please try again."
      );
    }
  };

  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => setTimer((t) => t - 1), 1000);
      return () => clearInterval(interval);
    }
  }, [timer]);

  return (
    <div className="reset-password-container">
      <div style={{ width: "100%" }}>
        <div className="back-button-container">
          <img src={Back} alt="Back" onClick={onBack} />
        </div>
      </div>
      <h2 className="login-title">Reset Your Password</h2>
      <p className="info-text">
        Enter the OTP sent to [{email}] and choose your new password.
      </p>

      {message && <div className={`alert ${messageType}`}>{message}</div>}

      <form onSubmit={handleResetPassword}>
        <div className="login-form-group">
          <label className="login-label">OTP</label>
          <input
            type="text"
            className="login-input"
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
            placeholder="Enter OTP"
            required
          />
        </div>

        <div className="login-form-group">
          <label className="login-label">New Password</label>
          <div className="login-input-container">
            <input
              type={showNewPassword ? "text" : "password"}
              className="login-input"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter New Password"
              required
            />
            {newPassword.length !== 0 && (
              <span
                className="eye-icon"
                onClick={() => setShowNewPassword((prev) => !prev)}
              >
                {showNewPassword ? <FaEyeSlash /> : <FaEye />}
              </span>
            )}
          </div>
          {newPassword.length > 0 && (
            <>
              <small className="password-strength">{passwordStrength}</small>
              <div className="constraints-list">
                <p className={constraints.minLength ? "met" : "unmet"}>
                  ✔ Minimum 8 characters
                </p>
                <p className={constraints.hasUpper ? "met" : "unmet"}>
                  ✔ At least 1 uppercase letter
                </p>
                <p className={constraints.hasLower ? "met" : "unmet"}>
                  ✔ At least 1 lowercase letter
                </p>
                <p className={constraints.hasNumber ? "met" : "unmet"}>
                  ✔ At least 1 number
                </p>
                <p className={constraints.hasSpecial ? "met" : "unmet"}>
                  ✔ At least 1 special character
                </p>
              </div>
            </>
          )}
        </div>

        <div className="login-form-group">
          <label className="login-label">Confirm New Password</label>
          <div className="login-input-container">
            <input
              type={showConfirmPassword ? "text" : "password"}
              className="login-input"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm Password"
              required
            />
            {confirmPassword.length !== 0 && (
              <span
                className="eye-icon"
                onClick={() => setShowConfirmPassword((prev) => !prev)}
              >
                {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
              </span>
            )}
          </div>
        </div>

        {newPassword && confirmPassword && !passwordsMatch && (
          <div className="password-warning">Passwords do not match</div>
        )}

        <div className="action-buttons">
          <button
            type="submit"
            className="submit-btn"
            disabled={!isFormComplete || loading}
          >
            {loading ? "Resetting..." : "Reset Password"}
          </button>
        </div>

        <p
          className="info-text otp-text"
          onClick={handleRequestNewOtp}
          style={{
            cursor: timer > 0 ? "not-allowed" : "pointer",
            userSelect: timer > 0 ? "none" : "auto",
          }}
        >
          Didn’t receive an email? Click here to receive a new OTP
          {timer > 0 ? ` [${timer}s]` : ""}
        </p>

        {requestOtpError && (
          <p className="request-otp-error">{requestOtpError}</p>
        )}
        {requestOtpSuccess && (
          <p className="request-otp-success">{requestOtpSuccess}</p>
        )}
      </form>
    </div>
  );
};
